import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Loader2,
  CheckCircle2,
  Send,
  FileQuestion,
  ShieldCheck,
  ExternalLink,
  AlertTriangle,
  Clock,
  RefreshCw,
  ArrowRight,
  Upload,
  Mail,
  X
} from "lucide-react";
import Logo from "../components/Logo";
import SafeRichText from "@/components/SafeRichText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRANDING = {
  logo_url: null,
  primary_color: "#1a4d2e",
  background_color: "#f5f5f0",
  button_color: "#1a4d2e",
  text_color: "#1a1a1a",
  show_logo: true,
  show_business_name: true,
  font_family: "default",
  button_style: "rounded",
  submit_button_text: "Submit Application",
};

const FONT_STACKS = {
  default: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Playfair Display', 'Libre Baskerville', Georgia, serif",
  modern_sans: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  elegant_sans: "'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif",
};

const BUTTON_RADIUS = {
  rounded: "9999px",
  soft: "10px",
  square: "2px",
};

// Contrast safety: calculate relative luminance from hex color
function getLuminance(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const toLinear = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// Returns a safe text color given a background color and intended text color
function getSafeTextColor(bgColor, textColor) {
  try {
    const bgLum = getLuminance(bgColor);
    const txtLum = getLuminance(textColor);
    // WCAG contrast ratio
    const ratio = (Math.max(bgLum, txtLum) + 0.05) / (Math.min(bgLum, txtLum) + 0.05);
    // If contrast ratio is too low (< 3:1), override text
    if (ratio < 3) {
      return bgLum > 0.5 ? "#1a1a1a" : "#ffffff";
    }
    return textColor;
  } catch {
    return textColor;
  }
}

// Helper: extract option label from either string or {label, score} object
const getOptionLabel = (opt) => typeof opt === "object" && opt !== null ? (opt.label || "") : String(opt || "");

export default function IntakeFormPage() {
  const { formSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [clientPortalLink, setClientPortalLink] = useState(null); // For redirect after submission
  const [redirectCountdown, setRedirectCountdown] = useState(null); // Countdown for redirect
  const [formData, setFormData] = useState({});
  const [businessInfo, setBusinessInfo] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [hideBrandingBadge, setHideBrandingBadge] = useState(false);
  
  // Token error state - for expired/invalid/used tokens
  const [tokenError, setTokenError] = useState(null);
  
  // Scheduling link state (for pre_booking forms only)
  const [showSchedulingLink, setShowSchedulingLink] = useState(false);
  const [schedulingButtonText, setSchedulingButtonText] = useState("Schedule a Call");
  const [schedulingUrl, setSchedulingUrl] = useState(null);
  
  // Document handling state
  const [documentHandling, setDocumentHandling] = useState(null);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [documentChecklist, setDocumentChecklist] = useState({});
  
  // Agreement state
  const [agreementRequired, setAgreementRequired] = useState(false);
  const [agreementUrl, setAgreementUrl] = useState(null);
  const [agreementText, setAgreementText] = useState(null);
  const [agreementMode, setAgreementMode] = useState("link"); // "link" or "text"
  const [agreementInstructions, setAgreementInstructions] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementOpened, setAgreementOpened] = useState(false);
  const [agreementOpenedAt, setAgreementOpenedAt] = useState(null);
  const [agreementVersion, setAgreementVersion] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  
  // Consent checkbox state (for pre_booking forms)
  const [consentEnabled, setConsentEnabled] = useState(false);
  const [consentText, setConsentText] = useState("");
  const [consentRequired, setConsentRequired] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  
  // File upload state (for file_upload fields)
  const [uploadedFiles, setUploadedFiles] = useState({}); // { fieldLabel: [{ file_id, filename, ... }] }
  const [uploadingField, setUploadingField] = useState(null);
  
  // Turnstile state
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [turnstileSitekey, setTurnstileSitekey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Anonymous funnel-tracking session token (regenerated per page load → unique)
  const analyticsSessionRef = useRef(null);
  const trackedFocusRef = useRef(false);
  const trackedFillRef = useRef(false);

  useEffect(() => {
    fetchFormConfig();
    checkCaptchaStatus();
  }, [formSlug]);

  // Generate (or reuse) anonymous session token + emit "view" event on mount.
  // Token is stored in sessionStorage so a refresh within the same browser
  // tab session reuses the same id (counts as 1 unique visitor, but the
  // server still increments total_views via $inc on every ping).
  useEffect(() => {
    if (analyticsSessionRef.current) return;
    const storageKey = `heyintake.analytics.${formSlug}`;
    let uuid = null;
    try {
      uuid = window.sessionStorage.getItem(storageKey);
    } catch (e) {
      uuid = null;
    }
    if (!uuid) {
      const cryptoObj = typeof window !== "undefined" ? window.crypto : null;
      uuid = cryptoObj && cryptoObj.randomUUID
        ? cryptoObj.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try { window.sessionStorage.setItem(storageKey, uuid); } catch (e) { /* private mode */ }
    }
    analyticsSessionRef.current = uuid;
    // Best-effort: do not block render or surface errors to user
    axios
      .post(`${API}/intake/${formSlug}/track`, {
        event_type: "view",
        session_token: uuid,
      })
      .catch(() => {});
  }, [formSlug]);

  const trackFunnelEvent = (eventType) => {
    const token = analyticsSessionRef.current;
    if (!token) return;
    if (eventType === "focus" && trackedFocusRef.current) return;
    if (eventType === "fill" && trackedFillRef.current) return;
    if (eventType === "focus") trackedFocusRef.current = true;
    if (eventType === "fill") trackedFillRef.current = true;
    axios
      .post(`${API}/intake/${formSlug}/track`, {
        event_type: eventType,
        session_token: token,
      })
      .catch(() => {});
  };
  
  // Load Turnstile script dynamically
  useEffect(() => {
    if (requiresCaptcha && turnstileSitekey && !window.turnstile) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        renderTurnstile();
      };
      document.body.appendChild(script);
      
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    } else if (requiresCaptcha && turnstileSitekey && window.turnstile) {
      renderTurnstile();
    }
  }, [requiresCaptcha, turnstileSitekey]);
  
  const renderTurnstile = useCallback(() => {
    if (window.turnstile && turnstileRef.current && turnstileSitekey) {
      // Remove existing widget if any
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {}
      }
      
      // Render new widget
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSitekey,
        theme: "light",
        callback: (token) => {
          setTurnstileToken(token);
        },
        "error-callback": () => {
          setTurnstileToken(null);
          toast.error("CAPTCHA verification failed. Please try again.");
        },
        "expired-callback": () => {
          setTurnstileToken(null);
        }
      });
    }
  }, [turnstileSitekey]);
  
  const checkCaptchaStatus = async () => {
    try {
      const response = await axios.get(`${API}/intake/${formSlug}/captcha-status`);
      setRequiresCaptcha(response.data.requires_captcha);
      if (response.data.turnstile_sitekey) {
        setTurnstileSitekey(response.data.turnstile_sitekey);
      }
    } catch (error) {
      // Don't show error - just proceed without captcha check
      console.error("Error checking captcha status:", error);
    }
  };

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${API}/intake/${formSlug}`);
      setBusinessInfo({
        business_name: response.data.business_name,
        business_type: response.data.business_type
      });
      setFormConfig(response.data.form_config);
      
      // Set branding if available
      if (response.data.branding) {
        setBranding({ ...DEFAULT_BRANDING, ...response.data.branding });
      }
      
      // Hide branding badge for premium plans
      if (response.data.hide_branding_badge) {
        setHideBrandingBadge(true);
      }
      
      // Set document handling if available (post_booking forms only)
      if (response.data.document_handling && response.data.document_requirements?.length > 0) {
        setDocumentHandling(response.data.document_handling);
        setDocumentRequirements(response.data.document_requirements);
        
        // Initialize document checklist state
        const initialChecklist = {};
        response.data.document_requirements.forEach(doc => {
          initialChecklist[doc.id] = false;
        });
        setDocumentChecklist(initialChecklist);
      }
      
      // Set agreement requirement if enabled (post_booking forms only)
      if (response.data.agreement_required) {
        setAgreementRequired(true);
        
        // Get full agreement settings
        const settings = response.data.agreement_settings || {};
        setAgreementMode(settings.mode || "link");
        setAgreementVersion(settings.version || null);
        
        if (settings.mode === "text" && settings.agreement_text) {
          setAgreementText(settings.agreement_text);
        } else if (settings.agreement_url) {
          setAgreementUrl(settings.agreement_url);
        }
        
        // Legacy fallback
        if (!settings.mode && response.data.agreement_url) {
          setAgreementUrl(response.data.agreement_url);
          setAgreementMode("link");
        }
        
        if (settings.instructions || response.data.agreement_instructions) {
          setAgreementInstructions(settings.instructions || response.data.agreement_instructions);
        }
      }
      
      // Set scheduling link for pre_booking forms only
      if (response.data.show_scheduling_link && response.data.scheduling_url) {
        setShowSchedulingLink(true);
        setSchedulingUrl(response.data.scheduling_url);
        if (response.data.scheduling_button_text) {
          setSchedulingButtonText(response.data.scheduling_button_text);
        }
      }
      
      // Set consent checkbox for pre_booking forms
      if (response.data.consent_checkbox_enabled) {
        setConsentEnabled(true);
        setConsentText(response.data.consent_checkbox_text || "I agree to be contacted regarding my request");
        setConsentRequired(response.data.consent_checkbox_required || false);
      }
      
      // Initialize form data - handle both 'label' and 'name' properties
      const initialData = {};
      response.data.form_config.fields.forEach(field => {
        const fieldLabel = field.label || field.name;
        initialData[fieldLabel] = "";
      });
      setFormData(initialData);
    } catch (error) {
      toast.error("Form not found");
    } finally {
      setLoading(false);
    }
  };

  // Honeypot state (hidden from users, catches bots)
  const [honeypot, setHoneypot] = useState({
    website_url: "",
    fax_number: ""
  });

  // Handle redirect countdown to Client Portal after submission (portal mode only)
  useEffect(() => {
    if (redirectCountdown === null || redirectCountdown <= 0) return;
    
    const timer = setTimeout(() => {
      if (redirectCountdown === 1) {
        // Redirect to Client Portal
        if (clientPortalLink) {
          navigate(clientPortalLink);
        }
      } else {
        setRedirectCountdown(redirectCountdown - 1);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [redirectCountdown, clientPortalLink, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = formConfig.fields
      .filter(f => {
        if (!f.required) return false;
        if (f.field_type === "file_upload") {
          return !(uploadedFiles[f.label] || []).length;
        }
        return !formData[f.label];
      })
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }
    
    // Validate agreement acknowledgment if required (checkbox only - link opened is informational)
    if (agreementRequired && !agreementAccepted) {
      toast.error("Please agree to the terms before submitting");
      return;
    }
    
    // Validate consent checkbox if required
    if (consentEnabled && consentRequired && !consentAccepted) {
      toast.error("Please check the consent checkbox before submitting");
      return;
    }
    
    // Validate CAPTCHA if required
    if (requiresCaptcha && !turnstileToken) {
      toast.error("Please complete the CAPTCHA verification");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        form_data: formData,
        // Include honeypot fields - will be empty for real users
        website_url: honeypot.website_url,
        fax_number: honeypot.fax_number,
        // Anonymous analytics token so server can dedup the funnel "submit" event
        analytics_session_token: analyticsSessionRef.current,
      };
      
      // Include agreement data if agreement was required
      if (agreementRequired) {
        submitData.agreement = {
          required: true,
          accepted: agreementAccepted,
          accepted_at: agreementAccepted ? new Date().toISOString() : null,
          opened: agreementOpened,
          opened_at: agreementOpenedAt,
          mode: agreementMode,
          agreement_url: agreementMode === "link" ? agreementUrl : null,
          agreement_version: agreementVersion  // Track which version was accepted
        };
      }
      
      // Include consent data if consent checkbox was enabled
      if (consentEnabled) {
        submitData.consent = {
          enabled: true,
          text: consentText,
          required: consentRequired,
          accepted: consentAccepted,
          accepted_at: consentAccepted ? new Date().toISOString() : null
        };
      }
      
      // Include document checklist if document handling is enabled
      if (documentHandling && documentRequirements.length > 0) {
        const docItems = documentRequirements.map(doc => ({
          doc_id: doc.id,
          label: doc.label,
          required: doc.required,
          confirmed: documentChecklist[doc.id] || false
        }));
        submitData.documents = {
          items: docItems
        };
      }
      
      // Include Turnstile token if CAPTCHA was required
      if (turnstileToken) {
        submitData.turnstile_token = turnstileToken;
      }
      
      // Include lead files if any were uploaded
      const allLeadFiles = Object.values(uploadedFiles).flat();
      if (allLeadFiles.length > 0) {
        submitData.lead_files = allLeadFiles;
      }
      
      // Build API URL with token if present
      const token = searchParams.get("token");
      const apiUrl = token 
        ? `${API}/intake/${formSlug}?token=${encodeURIComponent(token)}`
        : `${API}/intake/${formSlug}`;
      
      const response = await axios.post(apiUrl, submitData);
      // Source of truth: form's thank_you_message (Fields tab rich text) → legacy branding fallback → API message
      setThankYouMessage(formConfig.thank_you_message || branding.thank_you_message || response.data.message);
      setSubmitted(true);
      toast.success("Form submitted successfully!");
      
      // For portal mode (document_handling.method === "portal"), redirect to Client Portal
      const method = documentHandling?.method || "none";
      if (method === "portal" && response.data.client_link) {
        // Set up redirect with 2 second countdown
        setClientPortalLink(response.data.client_link);
        setRedirectCountdown(2);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      
      // Handle 400 errors for invalid/expired/used tokens
      if (status === 400 && searchParams.get("token")) {
        // Backend returns { error_type: string, message: string }
        let errorType = "invalid";
        let errorMsg = "";
        
        if (typeof detail === 'object' && detail.error_type) {
          // New structured error format
          errorType = detail.error_type;
          errorMsg = detail.message || "";
        } else {
          // Legacy string format - parse to determine type
          errorMsg = typeof detail === 'string' ? detail : detail?.message || "";
          const lowerMsg = errorMsg.toLowerCase();
          
          if (lowerMsg.includes("already been used") || lowerMsg.includes("already used")) {
            errorType = "used";
          } else if (lowerMsg.includes("expired")) {
            errorType = "expired";
          }
        }
        
        setSubmitting(false);
        setTokenError({
          type: errorType,
          message: errorMsg
        });
        return;
      }
      
      // Check if CAPTCHA is now required (escalation)
      if (typeof detail === 'object' && detail.requires_captcha) {
        setRequiresCaptcha(true);
        if (detail.turnstile_sitekey) {
          setTurnstileSitekey(detail.turnstile_sitekey);
        }
        toast.error(detail.message || "Please complete the CAPTCHA verification");
        // Reset token so user has to complete CAPTCHA again
        setTurnstileToken(null);
        if (window.turnstile && turnstileWidgetId.current) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
        return;
      }
      
      // Handle both string and object error details
      const errorMessage = typeof detail === 'object' ? detail.message : (detail || "Failed to submit form");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (label, value) => {
    setFormData({ ...formData, [label]: value });
    trackFunnelEvent("fill");
  };

  // File upload handler
  const handleFileUpload = async (fieldLabel, files) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    setUploadingField(fieldLabel);
    
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const MAX_TOTAL_SIZE = 15 * 1024 * 1024;
    
    // Calculate current total size
    const currentTotal = Object.values(uploadedFiles).flat().reduce((sum, f) => sum + (f.file_size || 0), 0);
    
    const newFiles = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 5MB per file)`);
        continue;
      }
      if (currentTotal + file.size > MAX_TOTAL_SIZE) {
        toast.error("Total upload size limit reached (15MB)");
        break;
      }
      
      try {
        const formPayload = new FormData();
        formPayload.append("file", file);
        formPayload.append("field_label", fieldLabel);
        
        const response = await axios.post(
          `${API}/api/intake/${formSlug}/upload`,
          formPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        
        newFiles.push(response.data);
      } catch (error) {
        const msg = error.response?.data?.detail || "Upload failed";
        toast.error(`Failed to upload ${file.name}: ${msg}`);
      }
    }
    
    if (newFiles.length > 0) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldLabel]: [...(prev[fieldLabel] || []), ...newFiles]
      }));
      // Also update formData to include file references
      setFormData(prev => ({
        ...prev,
        [fieldLabel]: [...(prev[fieldLabel] || []), ...newFiles.map(f => f.filename)].join(", ")
      }));
    }
    
    setUploadingField(null);
  };

  // Helper to get full logo URL
  const getLogoUrl = () => {
    if (!branding.logo_url) return null;
    if (branding.logo_url.startsWith('http')) return branding.logo_url;
    return `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`;
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: branding.background_color || "#f5f5f0" }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: branding.primary_color || "#1a4d2e" }} />
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: branding.background_color || "#f5f5f0" }}
      >
        <Card className="max-w-md w-full mx-4 rounded-2xl">
          <CardContent className="p-8 text-center">
            <FileQuestion className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Form Not Found</h2>
            <p className="text-muted-foreground">This intake form doesn't exist or has been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Token error state - show friendly UI for expired/invalid/used tokens
  if (tokenError) {
    const errorConfig = {
      expired: {
        icon: Clock,
        title: "Link Expired",
        description: "This intake link has expired. Links are valid for 14 days after being sent.",
        iconBg: "bg-amber-100",
        iconColor: "text-amber-600"
      },
      used: {
        icon: CheckCircle2,
        title: "Already Submitted",
        description: "This intake form has already been submitted. You can only submit once per link.",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600"
      },
      invalid: {
        icon: AlertTriangle,
        title: "Invalid Link",
        description: "This intake link is not valid. It may have been copied incorrectly.",
        iconBg: "bg-red-100",
        iconColor: "text-red-600"
      }
    };
    
    const config = errorConfig[tokenError.type] || errorConfig.invalid;
    const IconComponent = config.icon;
    
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          backgroundColor: branding.background_color || "#f5f5f0",
          fontFamily: FONT_STACKS[branding.font_family] || FONT_STACKS.default,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="max-w-md w-full rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)]" style={{ backgroundColor: branding.background_color || "#ffffff" }}>
            <CardContent className="p-8 text-center">
              {/* Show logo if available */}
              {getLogoUrl() && (
                <div className="mb-6">
                  <img 
                    src={getLogoUrl()} 
                    alt={businessInfo?.business_name || "Business Logo"}
                    className="h-40 sm:h-56 lg:h-64 w-auto max-w-[400px] mx-auto object-contain animate-[fadeIn_0.5s_ease-out]"
                  />
                </div>
              )}
              
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${config.iconBg}`}>
                <IconComponent className={`w-8 h-8 ${config.iconColor}`} />
              </div>
              
              <h2 
                className="text-2xl font-bold mb-3" 
                style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a") }}
              >
                {config.title}
              </h2>
              
              <p 
                className="text-base mb-6"
                style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a"), opacity: 0.7 }}
              >
                {config.description}
              </p>
              
              <div 
                className="p-4 rounded-xl mb-6"
                style={{ backgroundColor: `${branding.primary_color}10` || "#f0f9f4" }}
              >
                <p 
                  className="text-sm font-medium"
                  style={{ color: branding.primary_color || "#1a4d2e" }}
                >
                  <RefreshCw className="w-4 h-4 inline-block mr-2" />
                  Need a new link? Contact {businessInfo?.business_name || "the business"} to request a fresh intake form.
                </p>
              </div>
              
              {businessInfo?.email && (
                <a
                  href={`mailto:${businessInfo.email}?subject=New Intake Link Request`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                  style={{ 
                    backgroundColor: branding.button_color || branding.primary_color || "#1a4d2e",
                    color: "#ffffff"
                  }}
                >
                  Request New Link
                </a>
              )}
            </CardContent>
          </Card>
          
          {/* HeyIntake branding */}
          {!hideBrandingBadge && (
            <div className="text-center mt-4">
              <a
                href="https://heyintake.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              >
                Powered by HeyIntake
              </a>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    const isPortalMode = (documentHandling?.method || "none") === "portal";
    const isEmailMode = (documentHandling?.method || "none") === "email";
    const hasDocuments = documentHandling?.documents?.length > 0 || documentRequirements?.length > 0;
    
    // Show redirect CTA if we have a client portal link AND documents are required
    // This applies to BOTH portal mode AND email mode
    const showPortalCTA = hasDocuments && clientPortalLink;
    
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          backgroundColor: branding.background_color || "#f5f5f0",
          fontFamily: FONT_STACKS[branding.font_family] || FONT_STACKS.default,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card 
            className="max-w-md w-full rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
            style={{ backgroundColor: branding.background_color || "#ffffff" }}
          >
            <CardContent className="p-8 text-center">
              {/* Show logo if available */}
              {getLogoUrl() && (
                <div className="mb-6">
                  <img 
                    src={getLogoUrl()} 
                    alt={businessInfo?.business_name || "Business Logo"}
                    className="h-40 sm:h-56 lg:h-64 w-auto max-w-[400px] mx-auto object-contain animate-[fadeIn_0.5s_ease-out]"
                  />
                </div>
              )}
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: `${branding.primary_color}20` }}
              >
                <CheckCircle2 className="w-8 h-8" style={{ color: branding.primary_color || "#1a4d2e" }} />
              </div>
              <h2 
                className="text-2xl font-bold mb-2" 
                style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a") }}
              >
                Request Submitted
              </h2>
              
              {/* Show CTA to continue to Client Portal if documents are required */}
              {showPortalCTA ? (
                <div className="mt-4 space-y-4">
                  <div 
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: `${branding.primary_color || "#1a4d2e"}15`,
                      border: `1px solid ${branding.primary_color || "#1a4d2e"}30`
                    }}
                  >
                    <p 
                      className="font-medium text-sm"
                      style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a") }}
                    >
                      Next Step: Submit Your Documents
                    </p>
                    <p 
                      className="text-sm mt-1"
                      style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a"), opacity: 0.7 }}
                    >
                      {isEmailMode 
                        ? "Review the required documents and follow the submission instructions."
                        : "Upload your documents to complete your request."}
                    </p>
                  </div>
                  
                  {/* Auto-redirect for portal mode only */}
                  {isPortalMode && redirectCountdown !== null && redirectCountdown > 0 && (
                    <div className="flex items-center justify-center gap-2" style={{ color: branding.primary_color || "#2563eb" }}>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Redirecting in {redirectCountdown}...</span>
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => navigate(clientPortalLink)}
                    className="w-full flex items-center justify-center gap-2 text-base py-6"
                    style={{ 
                      backgroundColor: branding.button_color || branding.primary_color || "#1a4d2e",
                      color: "#ffffff"
                    }}
                  >
                    Continue – Submit Documents
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <div
                  style={{ color: getSafeTextColor(branding.background_color || "#ffffff", branding.text_color || "#1a1a1a"), opacity: 0.85 }}
                  data-testid="thank-you-message-rendered"
                >
                  <SafeRichText html={thankYouMessage} />
                </div>
              )}
              
              {/* Scheduling link button (for pre_booking forms only) */}
              {showSchedulingLink && schedulingUrl && (
                <div className="mt-6">
                  <a 
                    href={schedulingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:opacity-90"
                    style={{ 
                      backgroundColor: branding.button_color || branding.primary_color || "#1a4d2e",
                      color: "#ffffff"
                    }}
                  >
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                    {schedulingButtonText}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-12 px-4"
      style={{ 
        backgroundColor: branding.background_color || "#f5f5f0",
        fontFamily: FONT_STACKS[branding.font_family] || FONT_STACKS.default,
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header — shown when at least one of logo/name is ON */}
        {(branding.show_logo !== false || branding.show_business_name !== false) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          {branding.show_logo !== false && (
            <div className="mx-auto mb-6">
              {getLogoUrl() ? (
                <img
                  src={getLogoUrl()}
                  alt={businessInfo?.business_name || "Business Logo"}
                  className="h-40 sm:h-56 lg:h-64 w-auto max-w-[400px] mx-auto object-contain animate-[fadeIn_0.5s_ease-out]"
                />
              ) : (
                <Logo size={64} style={{ color: branding.primary_color || "#1a4d2e" }} className="mx-auto" />
              )}
            </div>
          )}
          {branding.show_business_name !== false && (
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: getSafeTextColor(branding.background_color || "#f5f5f0", branding.text_color || "#1a1a1a") }}
            >
              {businessInfo?.business_name}
            </h1>
          )}
          {(formConfig.intro_text || branding.welcome_message) && (
            <div
              className="max-w-lg mx-auto"
              style={{ color: getSafeTextColor(branding.background_color || "#f5f5f0", branding.text_color || "#1a1a1a"), opacity: 0.85 }}
              data-testid="intro-text-rendered"
            >
              <SafeRichText html={formConfig.intro_text || branding.welcome_message} />
            </div>
          )}
        </motion.div>
        )}
        {/* Intro text (still shown even if header is off) */}
        {!(branding.show_logo !== false || branding.show_business_name !== false) &&
          (formConfig.intro_text || branding.welcome_message) && (
            <div
              className="max-w-lg mx-auto mb-8 text-center"
              style={{ color: getSafeTextColor(branding.background_color || "#f5f5f0", branding.text_color || "#1a1a1a"), opacity: 0.85 }}
              data-testid="intro-text-rendered"
            >
              <SafeRichText html={formConfig.intro_text || branding.welcome_message} />
            </div>
          )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <CardContent className="p-6 md:p-8">
              <form
                onSubmit={handleSubmit}
                onFocusCapture={() => trackFunnelEvent("focus")}
                className="space-y-6"
              >
                {formConfig.fields
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((field, index) => {
                    // Handle both 'label'/'name' and 'field_type'/'type' properties
                    const fieldLabel = field.label || field.name;
                    const fieldType = field.field_type || field.type;
                    const fieldId = field.id || `field-${index}`;
                    
                    return (
                    <motion.div
                      key={fieldId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="space-y-2"
                    >
                      <Label htmlFor={fieldId} className="text-sm font-medium">
                        {fieldLabel}
                        {field.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      
                      {fieldType === "text" && (
                        <Input
                          id={fieldId}
                          type="text"
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          placeholder={field.placeholder}
                          required={field.required}
                          className="h-12"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "email" && (
                        <Input
                          id={fieldId}
                          type="email"
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          placeholder={field.placeholder || "you@company.com"}
                          required={field.required}
                          className="h-12"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "phone" && (
                        <Input
                          id={fieldId}
                          type="tel"
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          placeholder={field.placeholder || "(555) 123-4567"}
                          required={field.required}
                          className="h-12"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "number" && (
                        <Input
                          id={fieldId}
                          type="number"
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          placeholder={field.placeholder}
                          required={field.required}
                          className="h-12"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "date" && (
                        <Input
                          id={fieldId}
                          type="date"
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          required={field.required}
                          className="h-12"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "textarea" && (
                        <Textarea
                          id={fieldId}
                          value={formData[fieldLabel] || ""}
                          onChange={(e) => updateField(fieldLabel, e.target.value)}
                          placeholder={field.placeholder}
                          required={field.required}
                          className="min-h-[120px]"
                          data-testid={`field-${fieldId}`}
                        />
                      )}
                      
                      {fieldType === "select" && (
                        <Select
                          value={formData[fieldLabel] || ""}
                          onValueChange={(value) => updateField(fieldLabel, value)}
                          required={field.required}
                        >
                          <SelectTrigger className="h-12" data-testid={`field-${fieldId}`}>
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map((option, oi) => {
                              const label = getOptionLabel(option);
                              return (
                                <SelectItem key={label || oi} value={label}>
                                  {label}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {fieldType === "radio" && (
                        <div className="space-y-2">
                          {(field.options || []).map((option, oi) => {
                            const label = getOptionLabel(option);
                            return (
                              <label key={label || oi} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                                <input
                                  type="radio"
                                  name={fieldId}
                                  value={label}
                                  checked={formData[fieldLabel] === label}
                                  onChange={(e) => updateField(fieldLabel, e.target.value)}
                                  required={field.required}
                                  className="w-4 h-4 text-primary"
                                />
                                <span className="text-sm">{label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      
                      {fieldType === "checkbox" && (
                        <div className="space-y-2">
                          {(field.options || []).map((option, oi) => {
                            const label = getOptionLabel(option);
                            return (
                              <label key={label || oi} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  value={label}
                                  checked={(formData[fieldLabel] || []).includes(label)}
                                  onChange={(e) => {
                                    const current = formData[fieldLabel] || [];
                                    if (e.target.checked) {
                                      updateField(fieldLabel, [...current, label]);
                                    } else {
                                      updateField(fieldLabel, current.filter(v => v !== label));
                                    }
                                  }}
                                  className="w-4 h-4 text-primary rounded"
                                />
                                <span className="text-sm">{label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* File Upload Field */}
                      {fieldType === "file_upload" && (
                        <div className="space-y-3">
                          <div
                            className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                            onClick={() => document.getElementById(`file-input-${index}`)?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                            onDrop={async (e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                              const files = Array.from(e.dataTransfer.files);
                              if (files.length > 0) {
                                await handleFileUpload(fieldLabel, field.multiple_files ? files : [files[0]]);
                              }
                            }}
                            data-testid={`file-drop-${index}`}
                          >
                            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              {uploadingField === fieldLabel ? "Uploading..." : "Drag & drop or click to upload"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, TXT, PNG, JPG (max 5MB)</p>
                            <input
                              id={`file-input-${index}`}
                              type="file"
                              accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.gif,.webp"
                              multiple={field.multiple_files || false}
                              className="hidden"
                              onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length > 0) {
                                  await handleFileUpload(fieldLabel, files);
                                  e.target.value = '';
                                }
                              }}
                              data-testid={`file-input-${index}`}
                            />
                          </div>
                          
                          {/* Uploaded files list */}
                          {(uploadedFiles[fieldLabel] || []).length > 0 && (
                            <div className="space-y-2">
                              {uploadedFiles[fieldLabel].map((uf, fi) => (
                                <div key={uf.file_id} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                                  <FileQuestion className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm truncate">{uf.filename}</p>
                                    <p className="text-xs text-muted-foreground">{(uf.file_size / 1024).toFixed(0)} KB</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setUploadedFiles(prev => ({
                                        ...prev,
                                        [fieldLabel]: prev[fieldLabel].filter((_, i) => i !== fi)
                                      }));
                                    }}
                                    className="text-red-400 hover:text-red-600 p-1"
                                    data-testid={`file-remove-${index}-${fi}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {uploadingField === fieldLabel && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Uploading...
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                  })}

                {/* Honeypot fields - hidden from users, catches bots */}
                {/* Document Section - Only for post_booking with documents */}
                {documentHandling && documentRequirements.length > 0 && (
                  <div className="space-y-4 p-4 border rounded-xl bg-slate-50/50" data-testid="document-section">
                    <div className="flex items-center gap-2 text-sm font-medium" style={{ color: branding.text_color || "#1a1a1a" }}>
                      <FileQuestion className="w-4 h-4" />
                      Required Documents
                    </div>
                    
                    {/* PORTAL MODE: Simple expectation message only */}
                    {documentHandling.method === "portal" && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-start gap-3">
                          <Upload className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-blue-800">
                              You'll be able to upload your documents securely after submitting this form.
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              This will only take a few seconds.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* EXTERNAL LINK MODE: Simple expectation message only */}
                    {documentHandling.method === "external_link" && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-start gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-blue-800">
                            You'll receive instructions after submitting this form to upload your documents.
                          </p>
                        </div>
                      </div>
                    )}
                      
                    {/* EMAIL MODE: Simple expectation message only */}
                    {documentHandling.method === "email" && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-start gap-3">
                          <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-blue-800">
                            You'll receive instructions after submitting this form to provide your documents.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Consent / Submission Terms (for pre_booking forms) */}
                {consentEnabled && (
                  <div className="mt-12 pt-10" data-testid="consent-section">
                    {/* Subtle separator — spacing-led, not a heavy border */}
                    <div className="mx-auto max-w-[640px] px-1">
                      <h3 className="text-base font-medium text-zinc-500 mb-5 tracking-tight">
                        Submission Terms
                      </h3>
                      {/* Admin-controlled rich-text body: bullets + soft note */}
                      <div
                        className="text-[13.5px] text-zinc-700 leading-[1.65] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:marker:text-zinc-400 [&_p]:my-2 [&_a]:text-indigo-600 [&_a]:underline [&_em]:text-zinc-500 [&_em]:not-italic"
                      >
                        <SafeRichText html={consentText} testId="consent-text-rendered" />
                      </div>

                      {/* Checkbox row — generous spacing above, full-row clickable, top-aligned */}
                      <label className="mt-8 flex items-start gap-3 cursor-pointer group select-none">
                        <span className="relative flex-shrink-0 mt-0.5">
                          <input
                            type="checkbox"
                            checked={consentAccepted}
                            onChange={(e) => setConsentAccepted(e.target.checked)}
                            className="peer w-[18px] h-[18px] rounded-[4px] border border-zinc-300 text-primary focus:ring-1 focus:ring-primary cursor-pointer transition-colors"
                            data-testid="consent-checkbox"
                          />
                        </span>
                        <span className="text-[14px] text-zinc-800 leading-snug group-hover:text-zinc-900 transition-colors">
                          I have read and agree to the Submission Terms.
                          {consentRequired && <span className="text-zinc-400 ml-1">*</span>}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Agreement Section */}
                {agreementRequired && (
                  <div className="border-t pt-6 mt-6" data-testid="agreement-section">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Agreement</h4>
                    
                    {/* Agreement Instructions */}
                    {agreementInstructions && (
                      <p className="text-sm text-gray-600 mb-4">{agreementInstructions}</p>
                    )}
                    
                    {/* View Agreement Button - Different behavior for link vs text mode */}
                    {agreementMode === "link" && agreementUrl ? (
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            // Track that link was opened
                            setAgreementOpened(true);
                            setAgreementOpenedAt(new Date().toISOString());
                            // Open agreement link in new tab
                            window.open(agreementUrl, '_blank', 'noopener,noreferrer');
                          }}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            agreementOpened 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          data-testid="view-agreement-btn"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {agreementOpened ? '✓ Agreement Viewed' : 'View Agreement'}
                        </button>
                        {!agreementOpened && (
                          <p className="text-xs text-slate-500 mt-2">
                            We recommend reviewing the agreement before submitting.
                          </p>
                        )}
                      </div>
                    ) : agreementMode === "text" && agreementText ? (
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            setShowAgreementModal(true);
                            setAgreementOpened(true);
                            setAgreementOpenedAt(new Date().toISOString());
                          }}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            agreementOpened 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          data-testid="view-agreement-btn"
                        >
                          <FileQuestion className="w-4 h-4" />
                          {agreementOpened ? '✓ Agreement Viewed' : 'View Agreement'}
                        </button>
                        {!agreementOpened && (
                          <p className="text-xs text-slate-500 mt-2">
                            We recommend reviewing the agreement before submitting.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">
                          Agreement not configured. Please contact the business.
                        </p>
                      </div>
                    )}
                    
                    {/* Agreement Checkbox */}
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreementAccepted}
                        onChange={(e) => setAgreementAccepted(e.target.checked)}
                        className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary"
                        data-testid="agreement-checkbox"
                      />
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">I have reviewed and agree to the terms.</span>
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                    </label>
                  </div>
                )}

                {/* Honeypot fields (hidden from real users, visible to bots) */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={honeypot.website_url}
                    onChange={(e) => setHoneypot({ ...honeypot, website_url: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <label htmlFor="fax_number">Fax Number</label>
                  <input
                    type="text"
                    id="fax_number"
                    name="fax_number"
                    value={honeypot.fax_number}
                    onChange={(e) => setHoneypot({ ...honeypot, fax_number: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Turnstile CAPTCHA - only shows when triggered by rate limiting */}
                {requiresCaptcha && turnstileSitekey && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2 text-sm text-[#666]">
                      <ShieldCheck className="w-4 h-4" />
                      <span>Please complete the security check</span>
                    </div>
                    <div 
                      ref={turnstileRef}
                      className="flex justify-center"
                      data-testid="turnstile-widget"
                    />
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base text-white"
                  style={{ 
                    backgroundColor: branding.button_color || "#1a4d2e",
                    borderRadius: BUTTON_RADIUS[branding.button_style] || BUTTON_RADIUS.rounded,
                  }}
                  disabled={submitting || (requiresCaptcha && !turnstileToken)}
                  data-testid="submit-form-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {branding.submit_button_text || "Submit Application"}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Powered by HeyIntake badge - hidden for premium plans */}
        {!hideBrandingBadge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-6 text-center"
          >
            <a 
              href="https://heyintake.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
              data-testid="powered-by-badge"
            >
              <span>Powered by</span>
              <span className="font-medium">HeyIntake</span>
            </a>
          </motion.div>
        )}
      </div>
      
      {/* Agreement Text Modal */}
      <Dialog open={showAgreementModal} onOpenChange={setShowAgreementModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="agreement-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              Terms & Agreement
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-gray-700">
              {agreementText}
            </div>
          </ScrollArea>
          <div className="flex justify-end pt-4 border-t">
            <Button 
              onClick={() => setShowAgreementModal(false)}
              data-testid="close-agreement-modal-btn"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
